import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ErrorActions } from '@error/store/error.slice';
import { ProductImportImages } from '@products/interfaces/product-import-images';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { PageResponse } from '@shared/interfaces/page-response.interface';

import { ProductImportService } from '../services/product-import.service';
import {
  ProductImportActions,
  ProductImportSelectors,
} from './product-import.slice';

@Injectable({ providedIn: 'root' })
export class ProductImportImagesEffects {
  loadProductImportImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductImportActions.loadProductImportImages.trigger),
      switchMap((action) =>
        this.productImportService.getProductImportImages(action.filter).pipe(
          map((response: PageResponse<ProductImportImages>) =>
            ProductImportActions.loadProductImportImages.success(response)
          ),
          catchError(() =>
            of(ProductImportActions.loadProductImportImages.failure())
          )
        )
      )
    )
  );

  saveProductImportImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductImportActions.saveProductImportImages.trigger),
      withLatestFrom(
        this.store.select(
          ProductImportSelectors.selectProductImportImagesFilter
        )
      ),
      switchMap(([action, filter]) =>
        this.productImportService.postProductImportImages(action.formData).pipe(
          map(() => {
            this.store.dispatch(
              ProductImportActions.loadProductImportImages.trigger({ filter })
            );
            this.snackBar.openFromComponent(SnackbarComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              duration: 3000,
              data: this.translateService.instant('SHARED.SAVE_SUCCESS'),
            });
            return ProductImportActions.saveProductImportImages.success();
          }),
          catchError((err) => {
            this.handleError('SHARED.FILE_UPLOAD.ERROR.GENERIC', err.error);
            return of(ProductImportActions.saveProductImportImages.failure());
          })
        )
      )
    )
  );

  deleteProductImportImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductImportActions.deleteProductImportImage.trigger),
      withLatestFrom(
        this.store.select(
          ProductImportSelectors.selectProductImportImagesFilter
        )
      ),
      switchMap(([action, filter]) =>
        forkJoin(
          action.fileNames.map((filename) =>
            this.productImportService.deleteProductImportImage(filename)
          )
        ).pipe(
          map(() => {
            this.store.dispatch(
              ProductImportActions.loadProductImportImages.trigger({ filter })
            );
            this.snackBar.openFromComponent(SnackbarComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              duration: 3000,
              data: this.translateService.instant(
                'PRODUCTS.IMPORT.DELETE_SUCCESS'
              ),
            });
            return ProductImportActions.deleteProductImportImage.success();
          }),
          catchError((err) => {
            this.handleError('PRODUCTS.IMPORT.DELETE_FAILED', err.error);
            return of(ProductImportActions.deleteProductImportImage.failure());
          })
        )
      )
    )
  );

  saveProductImportTables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductImportActions.saveProductImportTables.trigger),
      switchMap((action) =>
        this.productImportService.postProductImportTables(action.formData).pipe(
          map(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              duration: 3000,
              data: this.translateService.instant('SHARED.SAVE_SUCCESS'),
            });
            return ProductImportActions.saveProductImportTables.success();
          }),
          catchError((err) => {
            this.handleError('SHARED.FILE_UPLOAD.ERROR.GENERIC', err.error);
            return of(ProductImportActions.saveProductImportTables.failure());
          })
        )
      )
    )
  );

  validateProductImportTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductImportActions.validateProductImportTables.trigger),
      switchMap((action) =>
        this.productImportService
          .getProductImportTableValidationList(action.fileName)
          .pipe(
            map((response) =>
              ProductImportActions.validateProductImportTables.success(response)
            ),
            catchError(() =>
              of(ProductImportActions.validateProductImportTables.failure())
            )
          )
      )
    )
  );

  private handleError(defaultMessageKey: string, messageOrNull?: string) {
    const message: string =
      messageOrNull ?? this.translateService.instant(defaultMessageKey);

    this.store.dispatch(
      ErrorActions.handleError({
        errorCode: '',
        globalErrors: {},
        fieldErrors: {},
        message: message,
        traceId: 0,
      })
    );
  }

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly productImportService: ProductImportService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService
  ) {}
}
